import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';
import { showToast } from '../hooks/useToast';
import ThemeToggle from '../components/UI/ThemeToggle';
import LanguageSelector from '../components/UI/LanguageSelector';

const REQ_CHECKS = [
  { key: 'reqMinLength', test: (v: string) => v.length >= 8 },
  { key: 'reqUppercase', test: (v: string) => /[A-Z]/.test(v) },
  { key: 'reqLowercase', test: (v: string) => /[a-z]/.test(v) },
  { key: 'reqNumber', test: (v: string) => /\d/.test(v) },
  { key: 'reqSpecial', test: (v: string) => /[^A-Za-z0-9]/.test(v) },
] as const;

function PasswordChecklist({ password }: { password: string }) {
  const { t } = useTranslation('auth');
  return (
    <div className="mt-3 rounded-lg border border-border bg-surface-secondary p-3 space-y-1.5">
      <p className="text-[11px] font-semibold text-earth-600 uppercase tracking-wide">
        {t('passwordRequirements.heading')}
      </p>
      {REQ_CHECKS.map(({ key, test }) => {
        const met = password.length > 0 && test(password);
        return (
          <div key={key} className="flex items-center gap-2">
            <span className={met ? 'text-success' : 'text-earth-400'} aria-hidden="true">
              {met ? '✓' : '○'}
            </span>
            <span className={`text-[12px] ${met ? 'text-success' : 'text-earth-500'}`}>
              {t(`passwordRequirements.${key}`)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  ) : (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
  );
}

function PasswordField({
  value, onChange, placeholder, required,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full h-[42px] border border-border rounded-lg px-[14px] pr-10 text-[14px] text-text bg-surface focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 placeholder:text-earth-400"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-earth-400 hover:text-earth-600"
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        <EyeIcon open={show} />
      </button>
    </div>
  );
}

function LockIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <circle cx="7.5" cy="15.5" r="5.5" />
      <path d="M21 2l-9.6 9.6" />
      <path d="M15.5 7.5l3 3L22 7l-3-3" />
    </svg>
  );
}

export default function ResetPassword() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    const allChecksPassed = REQ_CHECKS.every(({ test }) => test(newPassword));
    if (!allChecksPassed) {
      setError('resetPassword.weakPassword');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('resetPassword.passwordMismatch');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post('/auth/reset-password', { token, newPassword, confirmPassword });
      navigate('/login', { replace: true, state: { messageKey: 'resetPassword.successMessage' } });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      if (axiosErr.response?.status === 400 || axiosErr.response?.status === 422) {
        setError('resetPassword.invalidToken');
      } else {
        showToast(t('resetPassword.errorGeneric'), 'error');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="flex min-h-screen">
      {/* LEFT — hero panel */}
      <div className="hidden md:flex flex-1 bg-primary-500 text-white relative overflow-hidden flex-col justify-center px-16 py-16">
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -140, right: -140, width: 460, height: 460, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ top: -50, right: -50, width: 300, height: 300, background: 'rgba(255,255,255,0.10)' }}
        />
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ bottom: -180, left: -120, width: 380, height: 380, background: 'rgba(255,255,255,0.08)' }}
        />

        <div className="relative">
          <div className="mb-6">
            <LockIcon />
          </div>
          <h1 className="font-display text-[56px] leading-[64px] font-semibold text-white tracking-[-0.01em]">
            {t('resetPassword.heroTitle')}
          </h1>
          <p className="font-display italic text-white/90 text-2xl mt-3 font-medium">
            &ldquo;{t('resetPassword.heroSubtitle')}&rdquo;
          </p>
        </div>

        <div className="absolute left-16 right-16 bottom-14 flex flex-col gap-3.5 text-white/90 text-sm">
          <div className="flex items-center gap-3">
            <ShieldIcon />
            <span>{t('resetPassword.hint1')}</span>
          </div>
          <div className="flex items-center gap-3">
            <KeyIcon />
            <span>{t('resetPassword.hint2')}</span>
          </div>
        </div>
      </div>

      {/* RIGHT — form panel */}
      <div className="w-full md:flex-1 bg-surface flex flex-col relative">
        <div className="absolute top-5 right-5 md:top-7 md:right-8 flex items-center gap-2 z-10">
          <ThemeToggle />
          <LanguageSelector />
        </div>

        {/* Mobile brand */}
        <div className="md:hidden flex flex-col items-center pt-20 pb-6 px-6">
          <span className="font-display text-[32px] font-semibold text-primary-600 tracking-[-0.01em]">
            Udbodhanam
          </span>
          <p className="font-display italic text-text-muted text-[15px] font-medium mt-1 text-center">
            &ldquo;Awakening through knowledge&rdquo;
          </p>
        </div>

        <div className="flex-1 flex flex-col md:justify-center px-6 pb-10 pt-4 md:px-[72px] md:py-[60px]">
          <div className="max-w-[440px] w-full mx-auto">
            {!token ? (
              /* No token — invalid link */
              <div>
                <h2 className="text-[28px] font-bold text-earth-800 tracking-[-0.015em] mb-3">
                  {t('resetPassword.title')}
                </h2>
                <p className="text-sm text-error mb-6">{t('resetPassword.invalidToken')}</p>
                <Link
                  to="/forgot-password"
                  className="inline-block bg-primary-500 text-white font-semibold rounded-lg px-5 py-2.5 text-sm hover:bg-primary-600"
                >
                  {t('resetPassword.requestNewLink')}
                </Link>
                <p className="mt-4 text-sm text-center">
                  <Link to="/login" className="text-primary-600 hover:underline">
                    {t('resetPassword.backToSignIn')}
                  </Link>
                </p>
              </div>
            ) : (
              /* Reset form */
              <>
                <h2 className="text-[36px] leading-[44px] font-bold text-earth-800 tracking-[-0.015em]">
                  {t('resetPassword.title')}
                </h2>

                <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                  <div>
                    <label className="block text-[13px] font-semibold text-earth-700 mb-1.5">
                      {t('resetPassword.newPassword')}
                    </label>
                    <PasswordField value={newPassword} onChange={setNewPassword} required />
                    <PasswordChecklist password={newPassword} />
                  </div>

                  <div>
                    <label className="block text-[13px] font-semibold text-earth-700 mb-1.5">
                      {t('resetPassword.confirmPassword')}
                    </label>
                    <PasswordField value={confirmPassword} onChange={setConfirmPassword} required />
                  </div>

                  {error && <p className="text-sm text-error">{t(error)}</p>}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary-500 text-white font-semibold rounded-lg py-2.5 text-sm hover:bg-primary-600 active:bg-primary-700 disabled:opacity-50"
                  >
                    {loading ? t('resetPassword.resetting') : t('resetPassword.submit')}
                  </button>
                </form>

                <p className="text-center mt-6 text-sm">
                  <Link to="/login" className="text-primary-600 hover:underline">
                    {t('resetPassword.backToSignIn')}
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
